import {
  CARRIER_INSURANCE_PERCENT_DEFAULT,
  RequestCarrierInsuranceQuoteSchema,
  type CarrierBidScope,
  type RequestCarrierInsuranceQuote,
} from '@magimp/shared';
import { request } from '../client.js';

/**
 * @aiPurpose typed wrappers around
 *   `/api/requests/:id/carriers/:rcId/insurance*`. #1163 widened the
 *   endpoint surface: POST creates a row (primary or override based on
 *   `body.scope`); PATCH/DELETE/reconfirm are keyed by `quoteId` so
 *   multiple rows of the same carrier are addressable.
 * @aiInvariant Default percent is sourced from
 *   `CARRIER_INSURANCE_PERCENT_DEFAULT` (= 1) so any UI form that omits
 *   the field hits the issue-mandated default at the wire layer. The
 *   wire schema rejects REQUEST + non-null `scopeRefId` and non-REQUEST
 *   + null/empty `scopeRefId`; the service additionally validates that
 *   the ref resolves to a sub-resource of THIS request.
 */

export interface UpsertCarrierInsuranceBody {
  /** 0..100, defaults to `CARRIER_INSURANCE_PERCENT_DEFAULT` (1). */
  percent?: number;
  /** Optional free-text; wire allows max 2000 chars and defaults to ''. */
  comment?: string;
}

export interface AddCarrierInsuranceBody extends UpsertCarrierInsuranceBody {
  /** Scope discriminator; defaults to `REQUEST` at the wire layer. */
  scope?: CarrierBidScope;
  /** Required (non-empty) when `scope !== REQUEST`; must be `null` for REQUEST. */
  scopeRefId?: string | null;
}

export const INSURANCE_PERCENT_DEFAULT = CARRIER_INSURANCE_PERCENT_DEFAULT;

export function addCarrierInsuranceQuote(
  id: string,
  requestCarrierId: string,
  body: AddCarrierInsuranceBody,
): Promise<RequestCarrierInsuranceQuote> {
  return request<RequestCarrierInsuranceQuote>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}/insurance`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierInsuranceQuoteSchema,
  );
}

export function patchCarrierInsuranceQuote(
  id: string,
  requestCarrierId: string,
  quoteId: string,
  body: UpsertCarrierInsuranceBody,
): Promise<RequestCarrierInsuranceQuote> {
  return request<RequestCarrierInsuranceQuote>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}/insurance/${encodeURIComponent(quoteId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierInsuranceQuoteSchema,
  );
}

export function reconfirmCarrierInsuranceQuote(
  id: string,
  requestCarrierId: string,
  quoteId: string,
): Promise<RequestCarrierInsuranceQuote> {
  return request<RequestCarrierInsuranceQuote>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}/insurance/${encodeURIComponent(quoteId)}/reconfirm`,
    { method: 'POST' },
    {},
    RequestCarrierInsuranceQuoteSchema,
  );
}

export function deleteCarrierInsuranceQuote(
  id: string,
  requestCarrierId: string,
  quoteId: string,
): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}/insurance/${encodeURIComponent(quoteId)}`,
    { method: 'DELETE' },
  );
}
